import {Dispatch, FC, MouseEvent, SetStateAction} from 'react'
import clsx from 'clsx'
import type {IInvoiceTypeTemplate} from './core/_models'
import type {IInvoiceFav, InvoiceTemplateChoice} from './InvoiceTypeSelect'
import {saveExtraSetting} from '../../../../../_metronic/helpers'
import {useAuth} from '../../../auth'

type Props = {
  template: IInvoiceTypeTemplate
  choices: InvoiceTemplateChoice[]
  fav: {
    fav: IInvoiceFav[]
    setFav: Dispatch<SetStateAction<IInvoiceFav[] | undefined>>
  }
  title: {
    title: string
    setTitle: Dispatch<SetStateAction<string>>
  }
  invoiceType: {
    invoiceType: number
    setInvoiceType: Dispatch<SetStateAction<number>>
  }
  invoiceTemplate: {
    invoiceTemplate: number
    setInvoiceTemplate: Dispatch<SetStateAction<number>>
  }
}

const getTypeHint = (typeId: number) =>
  typeId === 1 ? 'خریدار کامل و اعتبار مالیاتی' : 'خریدار اختیاری و بدون اعتبار'

const SelectInvoiceTypeBox: FC<Props> = ({
  template,
  choices,
  fav,
  title,
  invoiceType,
  invoiceTemplate,
}) => {
  const {setCurrentUser, saveAuth} = useAuth()
  const hasBothTypes =
    choices.some((choice) => choice.type.id === 1) && choices.some((choice) => choice.type.id === 2)
  const hasFavorite = choices.some((choice) =>
    fav.fav.some(
      (favorite) => favorite.type === choice.type.id && favorite.template === choice.template.id
    )
  )

  const selectChoice = (choice: InvoiceTemplateChoice) => {
    title.setTitle(`${choice.type.name} - ${choice.template.name}`)
    invoiceType.setInvoiceType(choice.type.id)
    invoiceTemplate.setInvoiceTemplate(choice.template.id)
  }

  const toggleFavorite = async (choice: InvoiceTemplateChoice, event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const previousFav = [...(fav.fav || [])]
    const nextFav = [...previousFav]
    const index = nextFav.findIndex(
      (item) => item.type === choice.type.id && item.template === choice.template.id
    )
    if (index >= 0) {
      nextFav.splice(index, 1)
    } else {
      nextFav.push({type: choice.type.id, template: choice.template.id})
    }

    fav.setFav(nextFav)
    try {
      const response = await saveExtraSetting('invoiceSelectFav', nextFav)
      saveAuth(response)
      setCurrentUser(response)
    } catch {
      fav.setFav(previousFav)
    }
  }

  return (
    <article className={clsx('mp-invoice-template-card', {favorite: hasFavorite})}>
      <div className='mp-invoice-template-head'>
        <span
          className={clsx('mp-invoice-template-icon', `bg-light-${template.color}`)}
          aria-hidden='true'
        >
          <i className={clsx('las', template.icon, `text-${template.color}`)}></i>
        </span>
        <span className='mp-invoice-template-copy'>
          <strong>{template.name}</strong>
          <span>{template.description}</span>
        </span>
        {hasFavorite && <span className='badge badge-light-warning'>پرکاربرد شما</span>}
      </div>

      <div className='mp-invoice-template-availability'>
        {hasBothTypes ? 'قابل صدور با نوع اول یا دوم' : 'قابل صدور فقط با نوع اول'}
      </div>

      <div className={clsx('mp-invoice-template-choices', {single: choices.length === 1})}>
        {choices.map((choice) => {
          const isFavorite = fav.fav.some(
            (item) => item.type === choice.type.id && item.template === choice.template.id
          )
          return (
            <div className='mp-invoice-template-choice' key={choice.type.id}>
              <button
                type='button'
                className={clsx('mp-invoice-template-choice-select', {
                  'type-one': choice.type.id === 1,
                  'type-two': choice.type.id === 2,
                })}
                onClick={() => selectChoice(choice)}
              >
                <span>
                  <strong>{choice.type.shortName}</strong>
                  <small>{getTypeHint(choice.type.id)}</small>
                </span>
                <i className='las la-angle-left' aria-hidden='true'></i>
              </button>
              <button
                type='button'
                className='mp-invoice-template-favorite'
                aria-pressed={isFavorite}
                aria-label={
                  isFavorite
                    ? `حذف ${choice.type.shortName} از انتخاب‌های پرکاربرد`
                    : `افزودن ${choice.type.shortName} به انتخاب‌های پرکاربرد`
                }
                title={isFavorite ? 'حذف از انتخاب‌های پرکاربرد' : 'افزودن به انتخاب‌های پرکاربرد'}
                onClick={(event) => toggleFavorite(choice, event)}
              >
                <i
                  className={clsx(
                    'las la-star',
                    isFavorite ? `text-${choice.type.color}` : 'text-gray-400'
                  )}
                ></i>
              </button>
            </div>
          )
        })}
      </div>
    </article>
  )
}

export {SelectInvoiceTypeBox}
